
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ContactIngantiSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactIngantiSupportDialog = ({ open, onOpenChange }: ContactIngantiSupportDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !message.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
        },
      });

      if (error) {
        throw error;
      }

      // Reset form
      setName("");
      setEmail("");
      setMessage("");
      
      // Show success dialog
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setEmail("");
    setMessage("");
    onOpenChange(false);
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open && !showSuccessDialog} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] bg-white text-black">
          <DialogHeader>
            <DialogTitle className="text-black text-xl font-semibold">Contact Ingant Support</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-black">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-white text-black border-gray-300"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-black">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white text-black border-gray-300"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message" className="text-black">Support Question</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your support question..."
                rows={4}
                className="bg-white text-black border-gray-300 resize-none"
                required
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="bg-white text-black border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary text-white hover:bg-primary/90"
              >
                {isSubmitting ? "Sending..." : "Send"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-white w-[360px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black w-[150px]">Message sent</AlertDialogTitle>
            <AlertDialogDescription className="text-black w-[330px]">
              Your support request has been sent successfully. We'll get back to you soon.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction 
            onClick={handleSuccessDialogClose}
            className="bg-primary text-white hover:bg-primary/90 w-[70px]"
          >
            OK
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ContactIngantiSupportDialog;
